import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Result, Typography, Row, Col, Card, Tooltip,
} from 'antd';
import {
  PhoneOutlined, LinkedinOutlined, InstagramOutlined,
  WhatsAppOutlined, UserOutlined, MailOutlined, EnvironmentOutlined, BankOutlined,
} from '@ant-design/icons';
import { micrositioService, fixUrl } from '../../services/api';
import { SiteFooter, SiteHeader, colapsarAgrupados, heroBackground, heroTextLayout, heroTextPadding } from './RematePage';
import PujaSpinner from '../../components/PujaSpinner';
import AuthContext from '../../context/AuthContext';
import { TIPOGRAFIAS, altoHeaderDe } from '../../utils/micrositioTema';
import { heroMargenSuperior } from '../../utils/hero';
import { useAvisarAltoAlPadre } from '../../hooks/useAvisarAltoAlPadre';
import { urlDeRed } from '../../utils/urlSegura';
import { REDES } from '../../utils/redesSociales';

const { Title, Text } = Typography;

const ContactoPage = () => {
  // Le avisa al sitio incrustante cuánto medimos, para que agrande el
  // iframe en vez de dejarlo clavado en los 800px del snippet.
  useAvisarAltoAlPadre();

  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const cargar = async () => {
      try {
        const res = await micrositioService.getBySlug(slug);
        setData(res.data);
      } catch (err) {
        // Sin `message.error` a proposito: esto no es una accion puntual que
        // falla, es la carga entera de la pantalla. El error se comunica con el
        // <Result> a pagina completa de mas abajo, que dice mas que un toast
        // que se va en tres segundos. Un toast ADEMAS seria ruido duplicado.
        setError(err.response?.status === 404 ? 'not_found' : 'server_error');
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [slug]);

  if (loading) {
    return <PujaSpinner size="lg" label="Cargando…" fondo="#fff" />;
  }

  if (error === 'not_found') return <Result status="404" title="Remate no encontrado" />;
  if (error) return <Result status="500" title="Error al cargar" />;

  const { evento, categorias } = data;
  const config = evento.configuracion_micrositio || {};
  const tipografia = TIPOGRAFIAS[config.tipografia_combo] || TIPOGRAFIAS.moderna;
  const tipoCabecera = config.cabecera || 'cabecera-1';
  // `altoHeaderDe` y no la tabla a mano: el panel calcula con el el margen
  // automatico que le MUESTRA al productor, y dos cuentas del mismo numero es
  // justo la divergencia que `micrositioTema.js` existe para matar.
  const headerHeight = altoHeaderDe(config);
  const isTwoRow = tipoCabecera === 'cabecera-2' || tipoCabecera === 'cabecera-3';
  // Hero desactivado en páginas internas → sin banner, header sólido arriba.
  const heroOff = !!config.hero_off_internas;
  const colorHeaderInitial = config.color_header_initial || 'transparent';
  const heroTransparente = !heroOff && !isTwoRow && colorHeaderInitial === 'transparent';
  const heroMarginTop = heroMargenSuperior(config, headerHeight, heroTransparente);
  const heroPaddingTop = heroTransparente ? headerHeight : 0;
  const configHeader = heroOff
    ? { ...config, color_header_initial: config.color_header || config.color_primary || '#1a5f2a' }
    : config;

  // Las consume `.micrositio-page .hero-zone` en index.css, que ademas les
  // pone el tope de mobile. Aca solo viaja el dato, que es lo unico que cambia
  // entre paginas.
  const heroVars = {
    '--hero-margin-top':  `${heroMarginTop}px`,
    '--hero-padding-top': `${heroPaddingTop}px`,
  };

  const tarjetas = (config.contacto_tarjetas || []).filter(t => t.nombre || t.titulo);
  const numCols = tarjetas.length <= 2 ? tarjetas.length || 1
    : tarjetas.length <= 4 ? 2
    : 3;
  const spanCol = Math.floor(24 / numCols);

  const redes = [
    // Las cinco redes de siempre —instagram, facebook, youtube, linkedin,
    // twitter— se mudaron a `utils/redesSociales.jsx`: la barra de bienvenida
    // del catálogo (`BarraBienvenida.jsx`) las necesita con el mismo armado de
    // `href` (ver el comentario de cabecera ahí, con la vuelta de
    // `startsWith('http')` repetido en siete copias que motivó `urlDeRed`).
    ...REDES,
    // wa.me solo acepta digitos: si viene "+54 9 379 456-7890" hay que limpiarlo,
    // igual que hace TarjetaContacto con el telefono. Por eso NO usa `urlDeRed`:
    // acá el valor se transforma, no se enruta. WhatsApp queda acá y no en la
    // lista compartida porque, por ahora, sólo lo usa esta página.
    { key: 'whatsapp', icon: <WhatsAppOutlined />, color: '#25d366', label: 'WhatsApp',  href: (v) => (v.startsWith('http') ? v : `https://wa.me/${v.replace(/\D/g, '')}`) },
  ].filter(r => config[`contacto_${r.key}`]);

  // Se deriva del listado COLAPSADO, igual que el catálogo. Sobre el crudo, un
  // lote agrupado con animales de dos cabañas metía en el menú una cabaña que
  // el catálogo no muestra: al clickearla el visitante caía en una grilla
  // vacía. La lista tiene que decir lo que el catálogo puede filtrar.
  const animalesMenu = colapsarAgrupados(data.animales || []);
  const razas = [...new Set(
    animalesMenu.filter(a => a.nom_raza).map(a => a.nom_raza)
  )].sort();
  // Vacío en un remate de una sola cabaña, y ahí el menú no se dibuja.
  const cabanas = [...new Set(
    animalesMenu.filter(a => a.nom_cabana).map(a => a.nom_cabana)
  )].sort();

  return (
    <div
      className="micrositio-page"
      style={{ fontFamily: tipografia.body, minHeight: '100vh', ...heroVars }}
    >
      <SiteHeader
        config={configHeader}
        tipografia={tipografia}
        logoUrl={evento.img_evento}
        evento={evento}
        categorias={categorias}
        razas={razas}
        cabanas={cabanas}
        scrolled={scrolled}
        slug={slug}
        isAuthenticated={isAuthenticated}
      />

      {/* ── HERO (oculto si hero_off_internas) ── */}
      {heroOff ? (
        <div className="hero-off-spacer" style={{ '--hero-off-header': `${headerHeight}px` }} />
      ) : (
        <>
          <div
            className="hero-zone"
            style={{
              background: heroBackground(config, evento.imgsec_evento ? fixUrl(evento.imgsec_evento) : null),
              minHeight: config.hero_altura || (heroTransparente ? 260 : 180),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              // column: el eje vertical lo maneja justifyContent, no alignItems.
              ...heroTextLayout(config, 'column'),
            }}
          >
            {/* Titulo y subtitulo van envueltos: el ajuste de separacion debe
                despegar el BLOQUE del borde del hero. Aplicado al <Title>
                suelto separaba el titulo del subtitulo, y el subtitulo quedaba
                pegado abajo — distinto de lo que muestra la vista previa. */}
            <div style={{ textAlign: 'center', paddingLeft: 24, paddingRight: 24, ...heroTextPadding(config) }}>
              <Title
                level={1}
                style={{
                  color: config.color_hero_text || '#ffffff',
                  fontFamily: tipografia.title,
                  fontSize: config.hero_text_size || 32,
                  margin: 0,
                  textTransform: config.text_transform_hero || 'none',
                }}
              >
                {evento.nombre_evento}
              </Title>
              <Text style={{ color: 'rgba(255,255,255,0.85)', fontFamily: tipografia.body, fontSize: 16, marginTop: 8, display: 'block' }}>
                Contactanos
              </Text>
            </div>
          </div>

          {/* ── DIVIDER ── */}
          <div style={{ height: 5, background: config.color_primary || '#1a5f2a' }} />
        </>
      )}

      {/* ── TARJETAS ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '64px 24px 48px' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Title
            level={1}
            style={{ fontFamily: tipografia.title, color: config.color_title || '#1a1a1a', marginBottom: 16 }}
          >
            CONTACTO
          </Title>
          <Title
            level={2}
            style={{ fontFamily: tipografia.body, color: config.color_paragraph || '#666', fontWeight: 400, fontSize: 16, margin: 0 }}
          >
            POR MAYOR INFORMACIÓN, CONSULTAS TÉCNICAS O COMERCIALES DEJAMOS A SU DISPOSICIÓN NUESTRAS VÍAS DE CONTACTO:
          </Title>
        </div>
        {tarjetas.length > 0 ? (
          <Row gutter={[24, 24]} justify={tarjetas.length < numCols ? 'center' : 'start'}>
            {tarjetas.map((t, idx) => (
              <Col key={idx} xs={24} sm={12} md={spanCol}>
                <TarjetaContacto
                  tarjeta={t}
                  config={config}
                  tipografia={tipografia}
                />
              </Col>
            ))}
          </Row>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text style={{ color: '#999', fontFamily: tipografia.body }}>
              No hay información de contacto disponible.
            </Text>
          </div>
        )}
      </div>

      {/* ── REDES SOCIALES GENERALES ── */}
      {redes.length > 0 && (
        <div
          style={{
            background: '#f5f5f5',
            padding: '40px 24px',
            textAlign: 'center',
            borderTop: `1px solid #e8e8e8`,
          }}
        >
          <Text
            style={{
              display: 'block',
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 2,
              color: '#999',
              marginBottom: 20,
              fontFamily: tipografia.body,
            }}
          >
            Seguinos en
          </Text>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
            {redes.map(r => (
              <Tooltip key={r.key} title={r.label}>
                <a
                  href={r.href(config[`contacto_${r.key}`])}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: r.color,
                    color: '#fff',
                    fontSize: 20,
                    transition: 'opacity 0.2s, transform 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  {r.icon}
                </a>
              </Tooltip>
            ))}
          </div>
        </div>
      )}

      <SiteFooter
        config={config}
        tipografia={tipografia}
        evento={evento}
        categorias={categorias}
        logoUrl={evento.img_evento}
        onCategoriaSelect={(id) => navigate(`/remate/${slug}`, { state: { categoriaFiltro: id } })}
        slug={slug}
      />
    </div>
  );
};


// ── Tarjeta individual ────────────────────────────────────────────────
const TarjetaContacto = ({ tarjeta, config, tipografia }) => {
  const colorPrimary = config.color_primary || '#1a5f2a';

  return (
    <Card
      style={{ borderRadius: 12, height: '100%', borderTop: `4px solid ${colorPrimary}` }}
      styles={{ body: { padding: '24px 20px' } }}
    >
      {tarjeta.titulo && (
        <Text
          style={{
            display: 'block',
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 1.5,
            color: colorPrimary,
            fontWeight: 700,
            fontFamily: tipografia.body,
            marginBottom: 12,
          }}
        >
          {tarjeta.titulo}
        </Text>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        {(tarjeta.nombre || tarjeta.direccion) && (
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: `${colorPrimary}18`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {tarjeta.nombre
              ? <UserOutlined style={{ fontSize: 20, color: colorPrimary }} />
              : <BankOutlined style={{ fontSize: 20, color: colorPrimary }} />
            }
          </div>
        )}
        <div>
          {tarjeta.nombre && (
            <Text strong style={{ fontFamily: tipografia.title, fontSize: 15, display: 'block' }}>
              {tarjeta.nombre}
            </Text>
          )}
          {tarjeta.especialidad && (
            <Text style={{ color: '#888', fontSize: 12, fontFamily: tipografia.body }}>
              {tarjeta.especialidad}
            </Text>
          )}
        </div>
      </div>

      {tarjeta.direccion && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: '#555', fontSize: 13, marginTop: 12, fontFamily: tipografia.body }}>
          <EnvironmentOutlined style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{tarjeta.direccion}</span>
        </div>
      )}

      {tarjeta.telefono && (
        <a
          href={`https://wa.me/${tarjeta.telefono.replace(/\D/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#25d366', fontSize: 13, marginTop: 12, fontFamily: tipografia.body, textDecoration: 'none' }}
        >
          <PhoneOutlined />
          {tarjeta.telefono}
        </a>
      )}

      {tarjeta.email && (
        <a
          href={`mailto:${tarjeta.email}`}
          style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#555', fontSize: 13, marginTop: 6, fontFamily: tipografia.body, textDecoration: 'none' }}
        >
          <MailOutlined />
          {tarjeta.email}
        </a>
      )}

      {(tarjeta.linkedin || tarjeta.instagram) && (
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          {tarjeta.linkedin && (
            <Tooltip title="LinkedIn">
              <a
                href={urlDeRed(tarjeta.linkedin, 'https://linkedin.com/in/') || undefined}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#0a66c2', fontSize: 20 }}
              >
                <LinkedinOutlined />
              </a>
            </Tooltip>
          )}
          {tarjeta.instagram && (
            <Tooltip title="Instagram">
              <a
                href={urlDeRed(tarjeta.instagram, 'https://instagram.com/') || undefined}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#e1306c', fontSize: 20 }}
              >
                <InstagramOutlined />
              </a>
            </Tooltip>
          )}
        </div>
      )}
    </Card>
  );
};


export default ContactoPage;
